import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

export const useModal = () => {
  const [openModal, setOpenModal] = useState(null);
  const modalRef = useRef(null);

  const closeModal = useCallback(() => {
    setOpenModal(null);
  }, []);

  const openModalWithId = useCallback((modalId) => {
    setOpenModal(modalId);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  }, [closeModal]);

  useEffect(() => {
    if (openModal) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openModal, handleKeyDown]);

  useEffect(() => {
    if (!openModal) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          closeModal();
        }
      });
    }, { threshold: 0.1 });

    const currentModalRef = modalRef.current;

    if (currentModalRef) {
      observer.observe(currentModalRef);
    }

    return () => {
      if (currentModalRef) {
        observer.unobserve(currentModalRef);
      }
    };
  }, [openModal, closeModal]);

  return useMemo(() => ({
    openModal,
    openModalWithId,
    closeModal,
    modalRef,
  }), [openModal, openModalWithId, closeModal]);
};